/**
 * Share text: a small emoji map of the expedition plus the distance, in the player's language.
 * See spec/core-spec.md, section "Share text".
 */
import type { Coord } from "./coords.ts";
import { formatDistance, type Locale } from "./distance.ts";
import type { Game } from "./game.ts";
import { floorDiv } from "./hash.ts";

export const SHARE_MAX_CELLS = 8;

export const SHARE_GLYPHS = {
  hidden: "⬛", // black large square
  revealed: "⬜", // white large square
  cabin: "🏠", // house
  killer: "🟥", // red square
} as const;

const SHARE_URL = "mistsweeper.com";

/**
 * Section 8's table: the headers and the footers, with `{n}`, `{distance}` and `{tiles}` for what
 * goes in them. The rows below English and Swedish are drafts until their languages are released.
 */
const STRINGS: Readonly<
  Record<Locale, { daily: string; endless: string; one: string; other: string }>
> = {
  en: {
    daily: "Mistsweeper, expedition #{n}",
    endless: "Mistsweeper, endless expedition",
    one: "{distance}, {tiles} tile",
    other: "{distance}, {tiles} tiles",
  },
  sv: {
    daily: "Mistsweeper, expedition #{n}",
    endless: "Mistsweeper, oändlig expedition",
    one: "{distance}, {tiles} ruta",
    other: "{distance}, {tiles} rutor",
  },
  de: {
    daily: "Mistsweeper, Expedition #{n}",
    endless: "Mistsweeper, endlose Expedition",
    one: "{distance}, {tiles} Feld",
    other: "{distance}, {tiles} Felder",
  },
  es: {
    daily: "Mistsweeper, expedición #{n}",
    endless: "Mistsweeper, expedición sin fin",
    one: "{distance}, {tiles} casilla",
    other: "{distance}, {tiles} casillas",
  },
  fr: {
    daily: "Mistsweeper, expédition #{n}",
    endless: "Mistsweeper, expédition sans fin",
    one: "{distance}, {tiles} case",
    other: "{distance}, {tiles} cases",
  },
  ja: {
    daily: "Mistsweeper 探検 #{n}",
    endless: "Mistsweeper 終わりなき探検",
    one: "{distance}、{tiles}マス",
    other: "{distance}、{tiles}マス",
  },
  ko: {
    daily: "Mistsweeper 탐험 #{n}",
    endless: "Mistsweeper 무한 탐험",
    one: "{distance}, {tiles}칸",
    other: "{distance}, {tiles}칸",
  },
  "zh-Hans": {
    daily: "Mistsweeper 探险 #{n}",
    endless: "Mistsweeper 无尽探险",
    one: "{distance}，{tiles}格",
    other: "{distance}，{tiles}格",
  },
  "zh-Hant": {
    daily: "Mistsweeper 探險 #{n}",
    endless: "Mistsweeper 無盡探險",
    one: "{distance}，{tiles}格",
    other: "{distance}，{tiles}格",
  },
};

/**
 * The emoji map: the bounding box of revealed tiles and the killer (banners do not count),
 * downsampled so the larger side has at most SHARE_MAX_CELLS cells. A cell is revealed when any
 * revealed tile falls in it. The cabin cell is marked, and the killer cell wins over everything.
 */
export function shareMap(game: Game): string[] {
  const tiles: Coord[] = game.revealedTiles();
  const killer = game.killer;
  let minX = 0;
  let maxX = 0;
  let minY = 0;
  let maxY = 0;
  for (const { x, y } of tiles) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (killer) {
    if (killer.x < minX) minX = killer.x;
    if (killer.x > maxX) maxX = killer.x;
    if (killer.y < minY) minY = killer.y;
    if (killer.y > maxY) maxY = killer.y;
  }
  const width = maxX - minX + 1;
  const height = maxY - minY + 1;
  const span = Math.max(width, height);
  const cell = Math.max(1, floorDiv(span + SHARE_MAX_CELLS - 1, SHARE_MAX_CELLS));
  const cols = floorDiv(width + cell - 1, cell);
  const rows = floorDiv(height + cell - 1, cell);
  const grid: string[][] = [];
  for (let r = 0; r < rows; r += 1) grid.push(new Array<string>(cols).fill(SHARE_GLYPHS.hidden));
  const put = (x: number, y: number, glyph: string) => {
    const row = grid[floorDiv(y - minY, cell)];
    if (row) row[floorDiv(x - minX, cell)] = glyph;
  };
  for (const { x, y } of tiles) put(x, y, SHARE_GLYPHS.revealed);
  put(0, 0, SHARE_GLYPHS.cabin);
  if (killer) put(killer.x, killer.y, SHARE_GLYPHS.killer);
  return grid.map((row) => row.join(""));
}

export interface ShareOptions {
  readonly locale: Locale;
  /** The expedition number for daily runs; ignored for endless runs. */
  readonly expedition?: number | null;
}

/** Header, map rows, distance and tile count, then the site. Lines joined with "\n". */
export function shareText(game: Game, options: ShareOptions): string {
  const strings = STRINGS[options.locale] ?? STRINGS.en;
  const header =
    game.mode === "daily" && options.expedition != null
      ? strings.daily.replace("{n}", String(options.expedition))
      : strings.endless;
  const footer = (game.tiles === 1 ? strings.one : strings.other)
    .replace("{distance}", formatDistance(game.metres, options.locale))
    .replace("{tiles}", String(game.tiles));
  return [header, ...shareMap(game), footer, SHARE_URL].join("\n");
}
